import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

type SimulationReadOnlyContextValue = {
  /** True while the version-history drawer is open. Drives the diff/preview highlight mode. */
  isVersionHistoryOpen: boolean;
  setIsVersionHistoryOpen: (value: boolean) => void;
  /** True while Logged-in user is not the simulation owner and is viewing the simulation in read-only mode. */
  isViewerMode: boolean;
  setIsViewerMode: (value: boolean) => void;
  isReadOnly: boolean;
  /** The version ID currently being previewed, or null for the live ("Current") view. */
  selectedVersionId: number | null;
  setSelectedVersionId: (id: number | null) => void;
};

const SimulationReadOnlyContext =
  createContext<SimulationReadOnlyContextValue | null>(null);

export const SimulationReadOnlyProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isViewerMode, setIsViewerMode] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
    null,
  );

  const value = useMemo<SimulationReadOnlyContextValue>(
    () => ({
      isVersionHistoryOpen,
      setIsVersionHistoryOpen,
      isViewerMode,
      setIsViewerMode,
      isReadOnly: isVersionHistoryOpen || isViewerMode,
      selectedVersionId,
      setSelectedVersionId,
    }),
    [isVersionHistoryOpen, isViewerMode, selectedVersionId],
  );

  return (
    <SimulationReadOnlyContext.Provider value={value}>
      {children}
    </SimulationReadOnlyContext.Provider>
  );
};

export const useSimulationReadOnly = (): SimulationReadOnlyContextValue => {
  const ctx = useContext(SimulationReadOnlyContext);
  if (!ctx) {
    throw new Error(
      'useSimulationReadOnly must be used within a SimulationReadOnlyProvider',
    );
  }
  return ctx;
};
